import React, {
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';

import {
  AppIcon,
} from '@folio/stripes/core';
import stripesFinalForm from '@folio/stripes/final-form';
import {
  AccordionSet,
  AccordionStatus,
  Col,
  Pane,
  Paneset,
  Row,
  ExpandAllButton,
} from '@folio/stripes/components';

import {
  NavigateBackPaneFirstMenu,
  FormPaneFooter,
  StyledFormPane,
  RecordLinkageEdit,
} from '../../common';
import {
  APP_NAME,
  ICON_KEYS,
  JOB_FIELDS,
  FULL_PANE_WIDTH,
  ASSIGN_TASK_FIELDS,
  JOB_STATUSES,
  RECORD_LINKAGE_PAGE_TYPE_JOB,
} from '../../../constants';
import {
  GeneralInformation,
  AssignTaskList,
} from './components';

const JobForm = ({
  form,
  pristine,
  submitting,
  handleSubmit,
  formTitle,
  metadata,
  onBack,
  values,
}) => {
  const {
    reset,
    change,
  } = form;
  const initialAccordionState = {
    general: true,
    metadata: false,
    assignTask: true,
  };

  const jobStatus = values[JOB_FIELDS.STATUS];
  const canAssignedTaskStatusChange = jobStatus !== JOB_STATUSES.CREATED;

  const getFirstMenu = useCallback(() => (
    <NavigateBackPaneFirstMenu onClickHandler={onBack} />
  ), [onBack]);

  const getFooter = useCallback(() => (
    <FormPaneFooter
      form={form}
      pristine={pristine}
      submitting={submitting}
      handleSubmit={handleSubmit}
      onCancel={reset}
    />
  ), [pristine, submitting, reset, handleSubmit, form]);

  const getAppIcon = useCallback(() => (
    <AppIcon
      app={APP_NAME}
      iconKey={ICON_KEYS.APP}
    />
  ), []);

  const applyJobDateToTasks = useCallback(() => {
    const jobStartDate = values[JOB_FIELDS.START_DUE_DATE_RANGE][0][JOB_FIELDS.START_DATE];
    const jobDueDate = values[JOB_FIELDS.START_DUE_DATE_RANGE][0][JOB_FIELDS.DUE_DATE];

    values[JOB_FIELDS.ASSIGN_TASK].map((task, index) => {
      change(`${JOB_FIELDS.ASSIGN_TASK}[${index}].${ASSIGN_TASK_FIELDS.START_DATE}`, jobStartDate);
      change(`${JOB_FIELDS.ASSIGN_TASK}[${index}].${ASSIGN_TASK_FIELDS.DUE_DATE}`, jobDueDate);

      return true;
    });

    return true;
  }, [change, values]);

  return (
    <form>
      <Paneset isRoot>
        <Pane
          appIcon={getAppIcon()}
          defaultWidth={FULL_PANE_WIDTH}
          firstMenu={getFirstMenu()}
          footer={getFooter()}
          paneTitle={formTitle()}
          onClose={reset}
        >
          <StyledFormPane>
            <AccordionStatus>
              <Row end="xs">
                <Col xs>
                  <ExpandAllButton />
                </Col>
              </Row>
              <AccordionSet initialStatus={initialAccordionState}>
                <GeneralInformation
                  applyJobDateToTasks={applyJobDateToTasks}
                  metadata={metadata}
                />
                <RecordLinkageEdit pageType={RECORD_LINKAGE_PAGE_TYPE_JOB} />
                <AssignTaskList
                  canAssignedTaskStatusChange={canAssignedTaskStatusChange}
                  change={change}
                />
              </AccordionSet>
            </AccordionStatus>
          </StyledFormPane>
        </Pane>
      </Paneset>
    </form>
  );
};

JobForm.propTypes = {
  formTitle: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  values: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  form: PropTypes.shape({
    change: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
  }),
  metadata: PropTypes.object,
};

JobForm.defaultProps = {
  metadata: null,
};

export default stripesFinalForm({
  initialValuesEqual: (a, b) => isEqual(a, b),
  navigationCheck: true,
  subscription: {
    values: true,
  },
})(JobForm);
